import { useAuthStore } from '@/stores/auth'
import { useSyncStore } from '@/stores/sync'
import axios, { type CreateAxiosDefaults } from 'axios'

/** Get the axios instance for communicating with PAPI */
export function getAxiosInstance(options: CreateAxiosDefaults = {}) {
	const instance = axios.create({
		withCredentials: true,
		baseURL: import.meta.env.VITE_API_ROOT,
		headers: {
			'X-Requested-With': 'XMLHTTPRequest',
		},
		...options,
	})

	/** In local environment, add pUserToken to request from auth token */
	instance.interceptors.request.use(async (request) => {
		const token = await useAuthStore().getAuthToken()

		request.params = {
			...request.params,
			pUserToken: token,
		}
		return request
	})

	instance.interceptors.response.use(
		(response) => {
			// If we get a new access token, save it for later use
			const token = response.headers['x-access-token']

			if (token) {
				useAuthStore().setAuthToken(token)
			}
			return response
		},
		(error) => {
			if (error.response?.status === 401) {
				const auth = useAuthStore()
				if (auth.user) auth.sessionExpired = true
			}

			return Promise.reject(error)
		}
	)

	instance.interceptors.response.use(
		(response) => {
			const sync = useSyncStore()
			const url = response.config.url ?? ''

			// Clear server error if it existed and now succeeded
			if (sync.serverErrors[url]) {
				sync.clearServerError(url)
			}

			// Any successful response means we're online
			sync.offline = false
			return response
		},
		(error) => {
			const sync = useSyncStore()
			const url = error.config?.url ?? ''

			if (axios.isAxiosError(error)) {
				const res = error.response

				if (!res) {
					// No response means network failure (offline)
					sync.offline = true
				} else if (res.status === 401) {
					// Skip showing 401 (unauthorized) errors because we already have an alert prompting them to log back in
					sync.offline = false
				} else if (res.status >= 400) {
					// Server error (still online, but store/show error)
					sync.offline = false
					sync.setServerError(url, res.status, res.data?.message)
				}
			}

			return Promise.reject(error)
		}
	)

	return instance
}

export const axiosInstance = getAxiosInstance()
